import React from 'react'

import { AutomatedRuleActionType } from 'src/graphql/types/automatedRules'
import IconApplicant from 'src/icons/IconApplicant'
import IconBook from 'src/icons/IconBook'
import IconChecklist from 'src/icons/IconChecklist'
import IconClipboardList from 'src/icons/IconClipboardList'
import IconDocument from 'src/icons/IconDocument'
import IconEmail from 'src/icons/IconEmail'
import IconGraduationCap from 'src/icons/IconGraduationCap'
import IconMessage from 'src/icons/IconMessage'
import IconNote from 'src/icons/IconNote'
import IconPhone from 'src/icons/IconPhone'
import IconSwapArrows from 'src/icons/IconSwapArrows'
import IconUsers from 'src/icons/IconUsers'

export const ACTION_ICON_CLASS =
  'h-4 w-4 fill-none stroke-doubleNickel-gray-400'

export const ACTION_SORT_ORDER: Record<string, number> = {
  [AutomatedRuleActionType.SMS]: 1,
  [AutomatedRuleActionType.SEND_EMAIL]: 2,
  [AutomatedRuleActionType.SEND_VOICE_AGENT_CALL]: 3,
  [AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE]: 4,
  [AutomatedRuleActionType.GENERATE_ORDER]: 5,
  [AutomatedRuleActionType.ASSIGN_TRAINING]: 6,
  [AutomatedRuleActionType.ASSIGN_COURSE]: 7,
  [AutomatedRuleActionType.CHANGE_APPLICANT_STATUS]: 8,
  [AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE]: 9,
  [AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE]: 10,
  [AutomatedRuleActionType.CREATE_TASK]: 11,
  [AutomatedRuleActionType.ADD_NOTE]: 12,
}

export const ACTION_ICON_MAP: Record<string, React.ReactNode> = {
  [AutomatedRuleActionType.SMS]: <IconMessage className={ACTION_ICON_CLASS} />,
  [AutomatedRuleActionType.SEND_EMAIL]: (
    <IconEmail className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE]: (
    <IconDocument className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.GENERATE_ORDER]: (
    <IconClipboardList className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.ASSIGN_TRAINING]: (
    <IconGraduationCap className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.ASSIGN_COURSE]: (
    <IconBook className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.SEND_VOICE_AGENT_CALL]: (
    <IconPhone className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.CHANGE_APPLICANT_STATUS]: (
    <IconSwapArrows className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE]: (
    <IconApplicant className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE]: (
    <IconUsers className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.CREATE_TASK]: (
    <IconChecklist className={ACTION_ICON_CLASS} />
  ),
  [AutomatedRuleActionType.ADD_NOTE]: (
    <IconNote className={ACTION_ICON_CLASS} />
  ),
}

export const ACTION_TYPE_LABEL: Record<string, string> = {
  [AutomatedRuleActionType.SMS]: 'Send SMS',
  [AutomatedRuleActionType.SEND_EMAIL]: 'Send Email',
  [AutomatedRuleActionType.SEND_VOICE_AGENT_CALL]: 'Voice Agent Call',
  [AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE]: 'Request E-Signature',
  [AutomatedRuleActionType.GENERATE_ORDER]: 'Order Background Check',
  [AutomatedRuleActionType.ASSIGN_TRAINING]: 'Assign Training',
  [AutomatedRuleActionType.ASSIGN_COURSE]: 'Assign Course',
  [AutomatedRuleActionType.SEND_TRAINING_REMINDER]: 'Send Training Reminder',
  [AutomatedRuleActionType.CHANGE_APPLICANT_STATUS]: 'Change Applicant Status',
  [AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE]: 'Reassign Lead Employee',
  [AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE]: 'Add Member Employee',
  [AutomatedRuleActionType.CREATE_TASK]: 'Create Task',
  [AutomatedRuleActionType.ADD_NOTE]: 'Add Note',
  [AutomatedRuleActionType.START_VOE_OUTREACH]: 'Start Employment Verification',
}

export type ActionColorTrio = {
  bg: string
  border: string
  text: string
}

// Pipeline-view color treatment per action type. The table view ignores this
// map and renders all icons in neutral gray. Hex values are inline where the
// design uses a hue (teal, violet) we don't yet have as a tailwind token.
export const ACTION_COLOR_MAP: Record<string, ActionColorTrio> = {
  [AutomatedRuleActionType.SMS]: {
    bg: 'bg-doubleNickel-brand-25',
    border: 'border-doubleNickel-brand-100',
    text: 'text-doubleNickel-brand-600',
  },
  [AutomatedRuleActionType.SEND_EMAIL]: {
    bg: 'bg-[#F0FBFA]',
    border: 'border-[#A6E4DD]',
    text: 'text-[#0E7C7B]',
  },
  [AutomatedRuleActionType.SEND_VOICE_AGENT_CALL]: {
    bg: 'bg-[#F4F3FF]',
    border: 'border-[#D9D6FE]',
    text: 'text-[#5925DC]',
  },
  [AutomatedRuleActionType.CHANGE_APPLICANT_STATUS]: {
    bg: 'bg-doubleNickel-warning-50',
    border: 'border-doubleNickel-warning-100',
    text: 'text-doubleNickel-warning-700',
  },
  [AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE]: {
    bg: 'bg-doubleNickel-warning-50',
    border: 'border-doubleNickel-warning-100',
    text: 'text-doubleNickel-warning-700',
  },
  [AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE]: {
    bg: 'bg-doubleNickel-warning-50',
    border: 'border-doubleNickel-warning-100',
    text: 'text-doubleNickel-warning-700',
  },
  [AutomatedRuleActionType.GENERATE_ORDER]: {
    bg: 'bg-doubleNickel-warning-50',
    border: 'border-doubleNickel-warning-100',
    text: 'text-doubleNickel-warning-700',
  },
  [AutomatedRuleActionType.DOCUMENT_TEMPLATE_PACKAGE]: {
    bg: 'bg-doubleNickel-gray-50',
    border: 'border-doubleNickel-gray-200',
    text: 'text-doubleNickel-gray-700',
  },
  [AutomatedRuleActionType.CREATE_TASK]: {
    bg: 'bg-doubleNickel-gray-50',
    border: 'border-doubleNickel-gray-200',
    text: 'text-doubleNickel-gray-700',
  },
  [AutomatedRuleActionType.ADD_NOTE]: {
    bg: 'bg-doubleNickel-gray-50',
    border: 'border-doubleNickel-gray-200',
    text: 'text-doubleNickel-gray-700',
  },
  [AutomatedRuleActionType.ASSIGN_TRAINING]: {
    bg: 'bg-doubleNickel-success-50',
    border: 'border-doubleNickel-success-100',
    text: 'text-doubleNickel-success-700',
  },
  [AutomatedRuleActionType.ASSIGN_COURSE]: {
    bg: 'bg-doubleNickel-success-50',
    border: 'border-doubleNickel-success-100',
    text: 'text-doubleNickel-success-700',
  },
  [AutomatedRuleActionType.SEND_TRAINING_REMINDER]: {
    bg: 'bg-doubleNickel-success-50',
    border: 'border-doubleNickel-success-100',
    text: 'text-doubleNickel-success-700',
  },
  [AutomatedRuleActionType.START_VOE_OUTREACH]: {
    bg: 'bg-doubleNickel-blue-50',
    border: 'border-[#B9E6FE]',
    text: 'text-doubleNickel-blue-700',
  },
}
